/* Acquisti — Ordini d'Acquisto.
 *
 * Lista ordini importata da Excel, con filtri (anno, descrizione, plesso, fornitore, ricerca),
 * import Excel, CRUD inline e collegamento al fornitore (pn_anagrafica).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api from "@/lib/api";

const DESCRIZIONI = ["Richiesta Prezzi", "Conferma Ordine"];
const PLESSI = ["Infanzia", "Primaria", "Scuola", "Comunità"];

const STATO_META = {
  bozza:    { label: "BOZZA",    bg: "#fff3e0", color: "#e65100", border: "#ef6c00" },
  spedito:  { label: "SPEDITO",  bg: "#e3f2fd", color: "#0d47a1", border: "#1565c0" },
  ricevuto: { label: "RICEVUTO", bg: "#e8f5e9", color: "#1b5e20", border: "#2e7d32" },
};
const StatoBadge = ({ stato }) => {
  const key = (stato || "bozza").toLowerCase();
  const m = STATO_META[key] || { label: (stato || "—").toUpperCase(), bg: "#eceff1", color: "#455a64", border: "#90a4ae" };
  return (
    <span data-testid={`stato-badge-${key}`}
          style={{ display: "inline-block", padding: "2px 8px", background: m.bg, color: m.color,
                   border: `1px solid ${m.border}`, fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
                   borderRadius: 3 }}>
      {m.label}
    </span>
  );
};

const fmtEuro = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const isoToYear = (iso) => (iso || "").slice(0, 4);
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AcquistiOrdiniPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filterAnno, setFilterAnno] = useState("");
  const [filterDesc, setFilterDesc] = useState("");
  const [filterPlesso, setFilterPlesso] = useState("");
  const [filterForn, setFilterForn] = useState("");
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(null); // ordine in invio email
  const [fornitori, setFornitori] = useState([]);

  const carica = async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = {};
      if (filterAnno) params.anno = parseInt(filterAnno, 10);
      if (filterDesc) params.descrizione = filterDesc;
      if (filterPlesso) params.plesso = filterPlesso;
      if (filterForn) params.fornitore_pk = filterForn;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/acquisti/ordini", { params });
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento ordini");
    } finally {
      setLoading(false);
    }
  };

  const caricaFornitori = async () => {
    try {
      const r = await api.get("/pn/anagrafica", { params: { limit: 5000 } });
      const arr = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      arr.sort((a, b) => (a.ragione_sociale || "").localeCompare(b.ragione_sociale || ""));
      setFornitori(arr);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { caricaFornitori(); }, []);
  useEffect(() => { carica(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterAnno, filterDesc, filterPlesso, filterForn]);

  const anni = useMemo(() => {
    const s = new Set();
    items.forEach((it) => { const y = isoToYear(it.data_documento); if (y) s.add(y); });
    return Array.from(s).sort().reverse();
  }, [items]);

  const totale = useMemo(() => items.reduce((s, it) => s + Number(it.totale_iva_esclusa || 0), 0), [items]);

  const elimina = async (it) => {
    if (!window.confirm(`Eliminare ordine #${it.id} (${it.descrizione || "—"} · ${fmtDate(it.data_documento)})?`)) return;
    try {
      await api.delete(`/acquisti/ordini/${it.id}`);
      await carica();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const cambiaStato = async (it, nuovoStato, conferma) => {
    if (conferma && !window.confirm(conferma)) return;
    try {
      await api.post(`/acquisti/ordini/${it.id}/stato`, { stato: nuovoStato });
      await carica();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore cambio stato");
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/acquisti/ordini/import-excel?mode=upsert", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data || {};
      alert(`Import completato\n• Inseriti: ${d.inserted || 0}\n• Aggiornati: ${d.updated || 0}`);
      await carica();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore import");
    }
  };
  // Funzione import mantenuta solo per uso programmatico futuro (UI rimossa)
  void handleImport;

  return (
    <div data-testid="acquisti-ordini-page" style={{ padding: 16 }}>
      <div style={{ background: "linear-gradient(90deg,#311b92,#5e35b1)", color: "white", padding: "12px 18px", fontSize: 14, fontWeight: 800, letterSpacing: 0.5, borderRadius: 4, marginBottom: 12 }}>
        📦 ORDINI D'ACQUISTO
      </div>

      {/* Toolbar */}
      <div style={{ background: "#f5f5f5", padding: 10, marginBottom: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: 4 }}>
        <input type="text" placeholder="🔍 Cerca descrizione, fornitore, nr doc, note..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") carica(); }}
               data-testid="ord-search"
               style={{ flex: "1 1 280px", padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }} />
        <select value={filterAnno} onChange={(e) => setFilterAnno(e.target.value)} data-testid="ord-anno" style={sel}>
          <option value="">Tutti gli anni</option>
          {anni.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filterDesc} onChange={(e) => setFilterDesc(e.target.value)} data-testid="ord-desc" style={sel}>
          <option value="">Tutte le descrizioni</option>
          {DESCRIZIONI.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterPlesso} onChange={(e) => setFilterPlesso(e.target.value)} data-testid="ord-plesso" style={sel}>
          <option value="">Tutti i plessi</option>
          {PLESSI.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterForn} onChange={(e) => setFilterForn(e.target.value)} data-testid="ord-forn" style={{ ...sel, maxWidth: 220 }}>
          <option value="">Tutti i fornitori</option>
          {fornitori.map((f) => (
            <option key={f.primary_key} value={f.primary_key}>{f.ragione_sociale}</option>
          ))}
        </select>
        <button onClick={carica} disabled={loading} data-testid="ord-refresh"
                style={{ padding: "6px 12px", background: "#5e35b1", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          {loading ? "…" : "🔄 Filtra"}
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#555" }} data-testid="ord-counter">
            <b>{items.length}</b> ordini · Totale: <b>€ {fmtEuro(totale)}</b>
          </div>
          <button onClick={() => setEditing({})} data-testid="ord-new"
                  style={{ padding: "6px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            + Nuovo Ordine
          </button>
        </div>
      </div>

      {err && (
        <div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, fontSize: 12, marginBottom: 8, borderRadius: 3 }}>{err}</div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", background: "white" }}>
          <thead>
            <tr style={{ background: "#311b92", color: "white" }}>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 50 }}>ID</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 70 }}>NR DOC</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 90 }}>DATA</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>DESCRIZIONE</th>
              <th style={{ textAlign: "right", padding: "6px 8px", width: 110 }}>TOTALE €</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>FORNITORE</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 80 }}>PLESSO</th>
              <th style={{ textAlign: "left", padding: "6px 8px", width: 110 }}>PAGAMENTO</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>STATO</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 160 }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#888", fontStyle: "italic" }}>
                Nessun ordine trovato. Usa <b>📥 Importa Excel</b> o <b>+ Nuovo Ordine</b> per iniziare.
              </td></tr>
            )}
            {!loading && items.map((it, i) => (
              <tr key={it.id} data-testid={`ord-row-${it.id}`} style={{ borderTop: "1px solid #eee", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace", color: "#5e35b1", fontWeight: 700 }}>{it.id}</td>
                <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace" }}>{it.nr_documento ?? "—"}</td>
                <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace" }}>{fmtDate(it.data_documento)}</td>
                <td style={{ padding: "5px 8px", fontWeight: 600 }}>
                  {it.descrizione || <span style={{ color: "#bbb" }}>—</span>}
                  {it.note && <div style={{ fontSize: 9, color: "#666", fontStyle: "italic", marginTop: 1 }}>{it.note}</div>}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: Number(it.totale_iva_esclusa) > 0 ? "#1b5e20" : "#999" }}>
                  € {fmtEuro(it.totale_iva_esclusa)}
                </td>
                <td style={{ padding: "5px 8px" }}>
                  {it.fornitore_ragione_sociale ? (
                    <>
                      <div style={{ fontWeight: 600 }}>{it.fornitore_ragione_sociale}</div>
                      {it.fornitore_piva && <div style={{ fontSize: 9, color: "#888", fontFamily: "monospace" }}>{it.fornitore_piva}</div>}
                    </>
                  ) : it.fornitore_codice_legacy != null ? (
                    <span style={{ fontFamily: "monospace", color: "#ef6c00", fontWeight: 700 }} title="Codice legacy non collegato all'anagrafica">
                      ⚠ Cod. {it.fornitore_codice_legacy}
                    </span>
                  ) : <span style={{ color: "#bbb" }}>—</span>}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "center" }}>
                  {it.plesso ? (
                    <span style={{ padding: "1px 6px", background: "#ede7f6", color: "#311b92", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>{it.plesso}</span>
                  ) : <span style={{ color: "#bbb" }}>—</span>}
                </td>
                <td style={{ padding: "5px 8px", fontFamily: "monospace", fontSize: 10 }}>{it.pagamento || <span style={{ color: "#bbb" }}>—</span>}</td>
                <td style={{ padding: "5px 8px" }}>
                  <StatoBadge stato={it.stato} />
                  {it.spedito_at && (
                    <div data-testid={`ord-spedito-at-${it.id}`} title="Data e ora di invio dell'ordine"
                         style={{ fontSize: 9, color: "#0d47a1", marginTop: 2, whiteSpace: "nowrap" }}>
                      📤 {fmtDateTime(it.spedito_at)}
                    </div>
                  )}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "center" }}>
                  {(() => {
                    const st = (it.stato || "bozza").toLowerCase();
                    const isBozza = st === "bozza";
                    const isSpedito = st === "spedito";
                    return (
                      <>
                        {isBozza && (
                          <button onClick={() => setSending(it)} data-testid={`ord-email-${it.id}`}
                                  title="Invia email all'ordine (passa a SPEDITO se esito positivo)"
                                  style={{ padding: "3px 8px", background: "#0d47a1", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>✉</button>
                        )}
                        {isSpedito && (
                          <button onClick={() => cambiaStato(it, "ricevuto", `Confermi ricezione merce per ordine #${it.id}?`)}
                                  data-testid={`ord-ricevuto-${it.id}`}
                                  title="Marca come merce RICEVUTA"
                                  style={{ padding: "3px 8px", background: "#1b5e20", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📦</button>
                        )}
                        {isSpedito && (
                          <button onClick={() => cambiaStato(it, "bozza", `Annullare la spedizione dell'ordine #${it.id} e riportarlo in BOZZA?`)}
                                  data-testid={`ord-annulla-${it.id}`}
                                  title="Annulla spedizione (riporta a BOZZA per modifiche)"
                                  style={{ padding: "3px 8px", background: "#ef6c00", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>↩</button>
                        )}
                        <button onClick={() => {
                          const API = process.env.REACT_APP_BACKEND_URL;
                          window.open(`${API}/api/acquisti/ordini/${it.id}/pdf`, "_blank");
                        }} data-testid={`ord-pdf-${it.id}`}
                                title="Stampa PDF" style={{ padding: "3px 8px", background: "#6a1b9a", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>📄</button>
                        {isBozza && (
                          <button onClick={() => setEditing(it)} data-testid={`ord-edit-${it.id}`}
                                  title="Modifica (solo in stato BOZZA)" style={{ padding: "3px 8px", background: "#1565c0", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>✏</button>
                        )}
                        {isBozza && (
                          <button onClick={() => elimina(it)} data-testid={`ord-del-${it.id}`}
                                  title="Elimina (solo in stato BOZZA)" style={{ padding: "3px 8px", background: "#c62828", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>🗑</button>
                        )}
                      </>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <OrdineEditor ordine={editing} fornitori={fornitori}
                      onClose={() => setEditing(null)}
                      onSaved={async () => { setEditing(null); await carica(); }} />
      )}
      {sending && (
        <SendEmailModal ordine={sending} fornitori={fornitori}
                        onClose={() => setSending(null)}
                        onSent={async () => { setSending(null); await carica(); }} />
      )}
    </div>
  );
}

const sel = { padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3 };

function OrdineEditor({ ordine, fornitori, onClose, onSaved }) {
  const isNew = !ordine.id;
  const [doc, setDoc] = useState({
    nr_documento: ordine.nr_documento ?? "",
    data_documento: ordine.data_documento || "",
    descrizione: ordine.descrizione || "Conferma Ordine",
    totale_iva_esclusa: ordine.totale_iva_esclusa ?? 0,
    fornitore_pk: ordine.fornitore_pk || "",
    fornitore_codice_legacy: ordine.fornitore_codice_legacy ?? "",
    fornitore_email: ordine.fornitore_email || ordine.fornitore_email_anagrafica || "",
    pagamento: ordine.pagamento ?? "",
    stato: ordine.stato || "",
    plesso: ordine.plesso || "",
    note: ordine.note || "",
  });
  const [righe, setRighe] = useState(Array.isArray(ordine.righe) ? ordine.righe : []);
  const [prodotti, setProdotti] = useState([]);
  const [pickerRowIdx, setPickerRowIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setDoc((p) => ({ ...p, [k]: v }));

  // Carica i prodotti del fornitore selezionato (per autocomplete righe)
  const reloadProdotti = async () => {
    if (!doc.fornitore_pk) { setProdotti([]); return; }
    try {
      const r = await api.get("/acquisti/prodotti", { params: { fornitore_pk: doc.fornitore_pk } });
      setProdotti(r.data?.items || []);
    } catch { setProdotti([]); }
  };
  useEffect(() => { reloadProdotti(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [doc.fornitore_pk]);

  const totaleRighe = righe.reduce((s, r) => s + Number(r.quantita || 0) * Number(r.prezzo_unitario || 0), 0);

  const addRiga = () => setRighe([...righe, { codice: "", descrizione: "", unita_misura: "PZ", quantita: 1, prezzo_unitario: 0, prodotto_id: null }]);
  const rigaFileRef = useRef(null);
  const importRigheExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      const norm = (s) => String(s || "").trim().toUpperCase().replace(/['’`.]/g, "").replace(/\s+/g, " ");
      // Trova la riga di intestazione (contiene "DESCRIZIONE")
      let hIdx = -1, cDesc = -1, cCod = -1, cQta = -1;
      for (let i = 0; i < aoa.length; i++) {
        const cells = (aoa[i] || []).map(norm);
        const d = cells.findIndex((c) => c.includes("DESCRIZIONE") || c === "PRODOTTO" || c === "ARTICOLO");
        if (d >= 0) {
          hIdx = i; cDesc = d;
          cCod = cells.findIndex((c) => c === "CODICE" || c === "COD" || c === "CODART");
          cQta = cells.findIndex((c) => c.includes("QUANTITA") || c === "QTA" || c === "QTA" || c === "QTÀ");
          break;
        }
      }
      if (hIdx < 0) { alert("Colonna 'DESCRIZIONE PRODOTTO' non trovata nel file Excel."); return; }
      const nuove = [];
      for (let i = hIdx + 1; i < aoa.length; i++) {
        const row = aoa[i] || [];
        const desc = String(row[cDesc] ?? "").trim();
        if (!desc) continue; // salta righe senza descrizione
        const cod = cCod >= 0 ? String(row[cCod] ?? "").trim() : "";
        let qta = cQta >= 0 ? Number(String(row[cQta] ?? "").replace(",", ".")) : 0;
        if (!isFinite(qta)) qta = 0;
        nuove.push({ codice: cod, descrizione: desc, unita_misura: "PZ", quantita: qta || 0, prezzo_unitario: 0, prodotto_id: null });
      }
      if (!nuove.length) { alert("Nessuna riga valida trovata (serve la colonna DESCRIZIONE PRODOTTO compilata)."); return; }
      setRighe((curr) => [...curr, ...nuove]);
      alert(`Importate ${nuove.length} righe dal file Excel.`);
    } catch (err) {
      alert("Errore lettura Excel: " + (err.message || err));
    }
  };  const updRiga = (i, k, v) => setRighe((curr) => curr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const delRiga = (i) => setRighe(righe.filter((_, idx) => idx !== i));
  const applyProdotto = (i, p) => {
    setRighe((curr) => curr.map((r, idx) => idx === i ? {
      ...r,
      prodotto_id: p.id,
      codice: p.codice || "",
      descrizione: p.descrizione,
      unita_misura: p.unita_misura || "PZ",
      prezzo_unitario: Number(p.prezzo_unitario || 0),
    } : r));
  };
  const salvaRigaInCatalogo = async (i) => {
    const r = righe[i];
    if (!doc.fornitore_pk) { alert("Seleziona prima il fornitore."); return; }
    if (!(r.descrizione || "").trim()) { alert("Inserisci la descrizione del prodotto."); return; }
    if (r.prodotto_id) { alert("Questo prodotto è già in anagrafica."); return; }
    try {
      const resp = await api.post("/acquisti/prodotti/save-from-riga", {
        fornitore_pk: doc.fornitore_pk,
        codice: r.codice || null,
        descrizione: r.descrizione,
        unita_misura: r.unita_misura || "PZ",
        prezzo_unitario: Number(r.prezzo_unitario || 0),
      });
      // Aggancia il prodotto_id alla riga
      setRighe((curr) => curr.map((rr, idx) => idx === i ? { ...rr, prodotto_id: resp.data.id } : rr));
      await reloadProdotti();
      if (resp.data.duplicate) {
        alert("Prodotto già presente in catalogo: ho agganciato la riga al prodotto esistente.");
      } else {
        alert("✓ Prodotto aggiunto al catalogo del fornitore.");
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio prodotto");
    }
  };

  const salva = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        nr_documento: isNew ? null : (doc.nr_documento === "" ? null : Number(doc.nr_documento)),
        data_documento: isNew ? null : (doc.data_documento || null),
        descrizione: doc.descrizione || null,
        totale_iva_esclusa: Number(doc.totale_iva_esclusa || 0),
        fornitore_pk: doc.fornitore_pk || null,
        fornitore_email: (doc.fornitore_email || "").trim() || null,
        pagamento: doc.pagamento || null,
        stato: doc.stato || null,
        plesso: doc.plesso || null,
        note: doc.note || null,
        righe: righe.map((r) => ({
          prodotto_id: r.prodotto_id || null,
          codice: r.codice || null,
          descrizione: r.descrizione || null,
          unita_misura: r.unita_misura || "PZ",
          quantita: Number(r.quantita || 0),
          prezzo_unitario: Number(r.prezzo_unitario || 0),
          note: r.note || null,
        })),
      };
      if (isNew) {
        await api.post("/acquisti/ordini", payload);
      } else {
        await api.put(`/acquisti/ordini/${ordine.id}`, payload);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="ord-edit-modal"
           style={{ background: "white", width: "100%", maxWidth: 1100, maxHeight: "92vh", overflow: "auto", borderRadius: 6 }}>
        <div style={{ background: "#311b92", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>
            {isNew ? "+ Nuovo Ordine d'Acquisto" : `✏ Modifica Ordine #${ordine.id}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {isNew ? (
            <div style={{ gridColumn: "1 / -1", background: "#ede7f6", border: "1px solid #b39ddb", color: "#311b92", padding: 8, fontSize: 11, borderRadius: 3 }}>
              ℹ <b>Numero ordine</b> e <b>data</b> vengono assegnati automaticamente dalla procedura (data = oggi).
            </div>
          ) : (
            <>
              <div>
                <label style={lbl}>NR DOCUMENTO</label>
                <input type="number" value={doc.nr_documento} onChange={(e) => set("nr_documento", e.target.value)} style={inp} data-testid="oe-nr" />
              </div>
              <div>
                <label style={lbl}>DATA DOCUMENTO</label>
                <input type="date" value={doc.data_documento} onChange={(e) => set("data_documento", e.target.value)} style={inp} data-testid="oe-data" />
              </div>
            </>
          )}
          <div>
            <label style={lbl}>DESCRIZIONE</label>
            <select value={doc.descrizione} onChange={(e) => set("descrizione", e.target.value)} style={inp} data-testid="oe-desc">
              <option value="">—</option>
              {DESCRIZIONI.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>STATO</label>
            <div style={{ padding: "6px 8px", border: "1px solid #ddd", background: "#fafafa", borderRadius: 3 }}>
              <StatoBadge stato={doc.stato || "bozza"} />
              <span style={{ marginLeft: 8, fontSize: 10, color: "#888" }}>
                (transizione automatica all'invio email)
              </span>
            </div>
          </div>
          <div>
            <label style={lbl}>TOTALE IVA ESCLUSA €</label>
            <input type="number" step="0.01" value={doc.totale_iva_esclusa} onChange={(e) => set("totale_iva_esclusa", e.target.value)} style={inp} data-testid="oe-tot" />
          </div>
          <div>
            <label style={lbl}>PAGAMENTO</label>
            <input value={doc.pagamento} onChange={(e) => set("pagamento", e.target.value)} style={inp} placeholder="es. 60 gg, bonifico 30 gg DF..." data-testid="oe-pag" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>FORNITORE (anagrafica Prima Nota)</label>
            <select value={doc.fornitore_pk} onChange={(e) => {
                      const pk = e.target.value;
                      const f = fornitori.find((x) => x.primary_key === pk);
                      const femail = (f?.email || (f?.emails && f.emails[0]) || "").trim();
                      setDoc((p) => ({ ...p, fornitore_pk: pk, fornitore_email: femail || p.fornitore_email }));
                    }} style={inp} data-testid="oe-forn">
              <option value="">— Nessuno —</option>
              {fornitori.map((f) => <option key={f.primary_key} value={f.primary_key}>{f.ragione_sociale}{f.piva ? ` · ${f.piva}` : ""}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>EMAIL FORNITORE (usata per l'invio dell'ordine)</label>
            <input type="email" value={doc.fornitore_email} onChange={(e) => set("fornitore_email", e.target.value)}
                   style={inp} placeholder="fornitore@example.com" data-testid="oe-forn-email" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>PLESSO</label>
            <select value={doc.plesso} onChange={(e) => set("plesso", e.target.value)} style={inp} data-testid="oe-plesso">
              <option value="">—</option>
              {PLESSI.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {err && (<div style={{ gridColumn: "1 / -1", background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, fontSize: 11, borderRadius: 3 }}>{err}</div>)}
        </div>

        {/* RIGHE ORDINE */}
        <div style={{ borderTop: "2px solid #ede7f6", padding: "10px 16px 4px", background: "#fafafa" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#311b92", letterSpacing: 0.5 }}>📋 RIGHE ORDINE</div>
            <button type="button" onClick={() => rigaFileRef.current?.click()} data-testid="oe-import-righe-btn"
              title="Crea le righe da un file Excel (colonne: CODICE, DESCRIZIONE PRODOTTO, QUANTITÀ)"
              style={{ background: "#1b5e20", color: "white", border: "none", padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              📥 Importa righe da Excel
            </button>
            <input ref={rigaFileRef} type="file" accept=".xlsx,.xls" onChange={importRigheExcel} style={{ display: "none" }} data-testid="oe-import-righe-input" />
            {!doc.fornitore_pk && (
              <span style={{ fontSize: 10, color: "#c62828", fontStyle: "italic" }}>
                Seleziona prima un <b>fornitore</b> per attivare l'autocomplete dal catalogo prodotti.
              </span>
            )}
            {doc.fornitore_pk && (
              <span style={{ fontSize: 10, color: "#666" }}>
                Catalogo: <b>{prodotti.length}</b> prodotti
              </span>
            )}
          </div>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#311b92", color: "white" }}>
                <th style={{ width: 30, padding: 4 }}>#</th>
                <th style={{ width: 90, padding: 4, textAlign: "left" }}>CODICE</th>
                <th style={{ padding: 4, textAlign: "left" }}>DESCRIZIONE</th>
                <th style={{ width: 60, padding: 4 }}>UM</th>
                <th style={{ width: 80, padding: 4, textAlign: "right" }}>QTÀ</th>
                <th style={{ width: 90, padding: 4, textAlign: "right" }}>PREZZO €</th>
                <th style={{ width: 100, padding: 4, textAlign: "right" }}>IMPORTO €</th>
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {righe.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 12, textAlign: "center", color: "#888", fontStyle: "italic" }}>
                  Nessuna riga. Clicca <b>+ Aggiungi Riga</b> per inserire prodotti.
                </td></tr>
              )}
              {righe.map((r, i) => (
                <tr key={i} data-testid={`oe-riga-${i}`} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "2px 4px", textAlign: "center", color: "#888", fontFamily: "monospace" }}>{i + 1}</td>
                  <td style={{ padding: "2px 4px" }}>
                    <button type="button" onClick={() => doc.fornitore_pk && setPickerRowIdx(i)}
                            disabled={!doc.fornitore_pk}
                            data-testid={`oe-riga-pick-${i}`}
                            title={doc.fornitore_pk ? "Apri catalogo prodotti del fornitore" : "Seleziona prima il fornitore"}
                            style={{
                              width: "100%", padding: "3px 6px", border: "1px solid #5e35b1", borderRadius: 2,
                              fontSize: 11, background: doc.fornitore_pk ? "#ede7f6" : "#f5f5f5",
                              color: doc.fornitore_pk ? "#311b92" : "#999",
                              fontWeight: 700, cursor: doc.fornitore_pk ? "pointer" : "not-allowed", textAlign: "left",
                              fontFamily: r.codice ? "monospace" : "inherit",
                            }}>
                      {r.codice || (doc.fornitore_pk ? "🔍 Scegli…" : "—")}
                    </button>
                  </td>
                  <td style={{ padding: "2px 4px" }}>
                    <input value={r.descrizione || ""} onChange={(e) => updRiga(i, "descrizione", e.target.value)}
                           placeholder={doc.fornitore_pk ? "Clicca CODICE per scegliere dal catalogo" : "Descrizione libera"}
                           data-testid={`oe-riga-desc-${i}`} style={inpRig} />
                  </td>
                  <td style={{ padding: "2px 4px" }}>
                    <input value={r.unita_misura || "PZ"} onChange={(e) => updRiga(i, "unita_misura", e.target.value.toUpperCase())}
                           data-testid={`oe-riga-um-${i}`} style={{ ...inpRig, textAlign: "center" }} maxLength={6} />
                  </td>
                  <td style={{ padding: "2px 4px" }}>
                    <input type="number" step="0.01" value={r.quantita ?? 0}
                           onChange={(e) => updRiga(i, "quantita", e.target.value)}
                           data-testid={`oe-riga-qta-${i}`} style={{ ...inpRig, textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "2px 4px" }}>
                    <input type="number" step="0.01" value={r.prezzo_unitario ?? 0}
                           onChange={(e) => updRiga(i, "prezzo_unitario", e.target.value)}
                           data-testid={`oe-riga-prz-${i}`} style={{ ...inpRig, textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "2px 4px", textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: "#1b5e20" }}>
                    € {fmtEuro(Number(r.quantita || 0) * Number(r.prezzo_unitario || 0))}
                  </td>
                  <td style={{ padding: "2px 4px" }}>
                    <div style={{ display: "flex", gap: 3, justifyContent: "center", alignItems: "center", whiteSpace: "nowrap" }}>
                      {!r.prodotto_id && (r.descrizione || "").trim() && doc.fornitore_pk && (
                        <button type="button" onClick={() => salvaRigaInCatalogo(i)} data-testid={`oe-riga-savecat-${i}`}
                                title="Salva questa riga nel catalogo prodotti del fornitore"
                                style={{ background: "#ef6c00", color: "white", border: "none", padding: "2px 6px", fontSize: 10, cursor: "pointer", borderRadius: 2, lineHeight: 1 }}>💾</button>
                      )}
                      {r.prodotto_id && (
                        <span title="Prodotto già in catalogo" style={{ color: "#1b5e20", fontSize: 12, fontWeight: 700 }}>✓</span>
                      )}
                      <button type="button" onClick={() => delRiga(i)} data-testid={`oe-riga-del-${i}`}
                              style={{ background: "#c62828", color: "white", border: "none", padding: "2px 6px", fontSize: 10, cursor: "pointer", borderRadius: 2, lineHeight: 1 }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
              {righe.length > 0 && (
                <tr style={{ background: "#ede7f6", fontWeight: 800 }}>
                  <td colSpan={6} style={{ padding: "4px 8px", textAlign: "right", color: "#311b92" }}>TOTALE RIGHE</td>
                  <td style={{ padding: "4px 8px", textAlign: "right", color: "#311b92", fontFamily: "monospace" }}>€ {fmtEuro(totaleRighe)}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
          <div style={{ padding: "10px 4px 2px", display: "flex" }}>
            <button type="button" onClick={addRiga} data-testid="oe-add-riga"
                    style={{ marginLeft: "auto", padding: "6px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
              + Aggiungi Riga
            </button>
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="oe-save"
                  style={{ padding: "6px 16px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 2 }}>
            {saving ? "Salvataggio…" : "💾 Salva"}
          </button>
        </div>
      </div>

      {pickerRowIdx !== null && doc.fornitore_pk && (
        <ProdottoPickerModal
          fornitorePk={doc.fornitore_pk}
          fornitoreNome={(fornitori.find((f) => f.primary_key === doc.fornitore_pk) || {}).ragione_sociale || ""}
          onClose={() => setPickerRowIdx(null)}
          onPick={(p) => { applyProdotto(pickerRowIdx, p); setPickerRowIdx(null); reloadProdotti(); }}
          onChange={reloadProdotti}
        />
      )}
    </div>
  );
}

function ProdottoPickerModal({ fornitorePk, fornitoreNome, onClose, onPick, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(null); // {codice, descrizione, unita_misura, prezzo_unitario}

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get("/acquisti/prodotti", { params: { fornitore_pk: fornitorePk, search: search.trim() || undefined } });
      setItems(r.data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fornitorePk]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((p) => `${p.codice || ""} ${p.descrizione || ""}`.toLowerCase().includes(s));
  }, [items, search]);

  const elimina = async (p) => {
    if (!window.confirm(`Eliminare prodotto "${p.descrizione}"?`)) return;
    try {
      await api.delete(`/acquisti/prodotti/${p.id}`);
      await carica(); onChange && onChange();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const salvaNuovo = async () => {
    if (!adding?.descrizione?.trim()) { alert("Descrizione obbligatoria"); return; }
    try {
      const r = await api.post("/acquisti/prodotti", {
        codice: adding.codice || null,
        descrizione: adding.descrizione,
        fornitore_pk: fornitorePk,
        unita_misura: adding.unita_misura || "PZ",
        prezzo_unitario: Number(adding.prezzo_unitario || 0),
      });
      setAdding(null);
      await carica(); onChange && onChange();
      // Auto-pick il nuovo prodotto
      if (r.data?.id) {
        const rr = await api.get(`/acquisti/prodotti`, { params: { fornitore_pk: fornitorePk } });
        const fresh = (rr.data?.items || []).find((p) => p.id === r.data.id);
        if (fresh) onPick(fresh);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="oe-picker-modal"
           style={{ background: "white", width: "100%", maxWidth: 900, maxHeight: "90vh", display: "flex", flexDirection: "column", borderRadius: 6 }}>
        <div style={{ background: "#5e35b1", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14 }}>🛒 Catalogo Prodotti</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>Fornitore: <b>{fornitoreNome}</b></div>
          </div>
          <button onClick={onClose} data-testid="oe-picker-close"
                  style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 10, background: "#f5f5f5", display: "flex", gap: 8, alignItems: "center", borderBottom: "1px solid #ddd" }}>
          <input type="text" autoFocus placeholder="🔍 Cerca codice o descrizione..."
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 data-testid="oe-picker-search"
                 style={{ flex: 1, padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }} />
          <button onClick={() => setAdding({ codice: "", descrizione: "", unita_misura: "PZ", prezzo_unitario: 0 })}
                  data-testid="oe-picker-add"
                  style={{ padding: "6px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            + Nuovo Prodotto
          </button>
          <div style={{ fontSize: 11, color: "#555" }}><b>{filtered.length}</b>/{items.length}</div>
        </div>

        {adding && (
          <div style={{ padding: 12, background: "#fff8e1", border: "1px solid #ffb300", borderBottom: "1px solid #ffb300" }}>
            <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 80px 110px auto auto", gap: 6, alignItems: "end" }}>
              <div>
                <label style={lblSm}>CODICE</label>
                <input value={adding.codice} onChange={(e) => setAdding({ ...adding, codice: e.target.value })} style={inpRig} data-testid="oe-picker-new-cod" />
              </div>
              <div>
                <label style={lblSm}>DESCRIZIONE *</label>
                <input value={adding.descrizione} onChange={(e) => setAdding({ ...adding, descrizione: e.target.value })} style={inpRig} data-testid="oe-picker-new-desc" autoFocus />
              </div>
              <div>
                <label style={lblSm}>UM</label>
                <input value={adding.unita_misura} onChange={(e) => setAdding({ ...adding, unita_misura: e.target.value.toUpperCase() })} style={inpRig} maxLength={6} />
              </div>
              <div>
                <label style={lblSm}>PREZZO €</label>
                <input type="number" step="0.01" value={adding.prezzo_unitario} onChange={(e) => setAdding({ ...adding, prezzo_unitario: e.target.value })} style={inpRig} />
              </div>
              <button onClick={() => setAdding(null)} style={{ padding: "5px 10px", background: "white", border: "1px solid #ccc", fontSize: 11, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
              <button onClick={salvaNuovo} data-testid="oe-picker-new-save"
                      style={{ padding: "5px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                💾 Salva e seleziona
              </button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto" }}>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: "#311b92", color: "white", zIndex: 1 }}>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 8px", width: 110 }}>CODICE</th>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>DESCRIZIONE</th>
                <th style={{ textAlign: "center", padding: "6px 8px", width: 60 }}>UM</th>
                <th style={{ textAlign: "right", padding: "6px 8px", width: 90 }}>PREZZO €</th>
                <th style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#888", fontStyle: "italic" }}>
                  {items.length === 0
                    ? "Nessun prodotto in catalogo per questo fornitore. Clicca + Nuovo Prodotto."
                    : "Nessun prodotto corrispondente alla ricerca."}
                </td></tr>
              )}
              {!loading && filtered.map((p, i) => (
                <tr key={p.id} data-testid={`oe-picker-row-${p.id}`}
                    style={{ borderTop: "1px solid #eee", background: i % 2 === 0 ? "white" : "#fafafa", cursor: "pointer" }}
                    onClick={() => onPick(p)}>
                  <td style={{ padding: "5px 8px", fontFamily: "monospace", color: "#311b92", fontWeight: 700 }}>{p.codice || <span style={{ color: "#bbb" }}>—</span>}</td>
                  <td style={{ padding: "5px 8px" }}>{p.descrizione}</td>
                  <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace" }}>{p.unita_misura}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace" }}>
                    {p.prezzo_unitario > 0 ? `€ ${fmtEuro(p.prezzo_unitario)}` : <span style={{ color: "#bbb" }}>—</span>}
                  </td>
                  <td style={{ padding: "5px 8px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => onPick(p)} data-testid={`oe-picker-pick-${p.id}`}
                            style={{ background: "#1b5e20", color: "white", border: "none", padding: "3px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4, borderRadius: 2 }}>
                      ✓ Scegli
                    </button>
                    <button onClick={() => elimina(p)} data-testid={`oe-picker-del-${p.id}`}
                            style={{ background: "#c62828", color: "white", border: "none", padding: "3px 8px", fontSize: 10, cursor: "pointer", borderRadius: 2 }}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 3 };
const inp = { width: "100%", padding: "6px 8px", border: "1px solid #bbb", borderRadius: 3, fontSize: 12, boxSizing: "border-box" };
const inpRig = { width: "100%", padding: "3px 6px", border: "1px solid #ccc", borderRadius: 2, fontSize: 11, boxSizing: "border-box" };
const lblSm = { fontSize: 9, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 2 };

function SendEmailModal({ ordine, fornitori, onClose, onSent }) {
  const forn = fornitori.find((f) => f.primary_key === ordine.fornitore_pk);
  const defaultTo = (ordine.fornitore_email || forn?.email || (forn?.emails && forn.emails[0]) || ordine.fornitore_email_anagrafica || "").trim();
  const nrDoc = ordine.nr_documento ?? "—";
  const dataDoc = ordine.data_documento ? ordine.data_documento.split("-").reverse().join("/") : "—";
  const fornNome = forn?.ragione_sociale || "Spett.le Fornitore";
  const defaultSubject = `Ordine d'acquisto Nr. ${nrDoc} del ${dataDoc} — Istituto Canossiano`;
  const defaultBody = `Spett.le ${fornNome},

in allegato Vi inviamo il nostro ordine d'acquisto Nr. ${nrDoc} del ${dataDoc}.

Pagamento: ${ordine.pagamento || "da concordare"}
Plesso di destinazione: ${ordine.plesso || "—"}

Per qualsiasi necessità non esitate a contattarci.

Cordiali saluti,
Istituto Canossiano`;

  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const previewPdf = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    // Anteprima dentro il modal email: deve mostrare ESATTAMENTE l'allegato (con firma+timbro)
    window.open(`${API}/api/acquisti/ordini/${ordine.id}/pdf?with_signature=1`, "_blank");
  };

  const invia = async () => {
    const toList = to.split(/[,;\n]/).map((e) => e.trim()).filter(Boolean);
    if (toList.length === 0) { setErr("Inserisci almeno un destinatario"); return; }
    const ccList = cc.split(/[,;\n]/).map((e) => e.trim()).filter(Boolean);
    if (!window.confirm(`Inviare l'ordine Nr. ${nrDoc} a:\n${toList.join("\n")}${ccList.length ? "\n\nCC:\n" + ccList.join("\n") : ""}\n\nProcedere?`)) return;
    setSending(true);
    setErr(null);
    try {
      const r = await api.post(`/acquisti/ordini/${ordine.id}/send-email`, {
        to: toList,
        cc: ccList.length ? ccList : null,
        subject,
        body,
      });
      const d = r.data || {};
      if (d.status === "sent") alert(`✓ Email inviata correttamente.`);
      else if (d.status === "mock") alert(`⚠ Email registrata in MOCK (Resend non configurato). Vai in Log Email per vederla.`);
      else alert(`✗ Errore invio: ${d.error || "sconosciuto"}`);
      onSent && onSent();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore invio email");
    } finally { setSending(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="ord-send-modal"
           style={{ background: "white", width: "100%", maxWidth: 720, maxHeight: "92vh", overflow: "auto", borderRadius: 6 }}>
        <div style={{ background: "#0d47a1", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14 }}>✉ Invia Ordine d'Acquisto</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>Nr. <b>{nrDoc}</b> · {dataDoc} · {fornNome}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 16, display: "grid", gap: 10 }}>
          {!defaultTo && (
            <div style={{ background: "#fff8e1", border: "1px solid #ffb300", color: "#6d4c00", padding: 8, fontSize: 11, borderRadius: 3 }}>
              ⚠ Il fornitore non ha un'email in anagrafica. Inseriscila manualmente sotto.
            </div>
          )}
          <div>
            <label style={lbl}>A * (separa più indirizzi con virgola)</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="ord-send-to" style={inp}
                   placeholder="fornitore@example.com" />
          </div>
          <div>
            <label style={lbl}>CC</label>
            <input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="ord-send-cc" style={inp}
                   placeholder="opzionale" />
          </div>
          <div>
            <label style={lbl}>OGGETTO</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ord-send-subj" style={inp} />
          </div>
          <div>
            <label style={lbl}>MESSAGGIO</label>
            <textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} data-testid="ord-send-body"
                      style={{ ...inp, fontFamily: "inherit", resize: "vertical", lineHeight: 1.5 }} />
          </div>
          <div style={{ background: "#ede7f6", border: "1px solid #b39ddb", color: "#311b92", padding: 8, fontSize: 11, borderRadius: 3 }}>
            📎 Allegato: <b>Ordine_{nrDoc}_{ordine.data_documento || ""}.pdf</b> (generato automaticamente)
            <button onClick={previewPdf} style={{ marginLeft: 10, padding: "2px 10px", background: "#5e35b1", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
              👁 Anteprima
            </button>
          </div>
          {err && (<div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, fontSize: 11, borderRadius: 3 }}>{err}</div>)}
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
          <button onClick={invia} disabled={sending} data-testid="ord-send-confirm"
                  style={{ padding: "6px 18px", background: "#0d47a1", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: sending ? "wait" : "pointer", borderRadius: 2 }}>
            {sending ? "Invio…" : "✉ Invia Email"}
          </button>
        </div>
      </div>
    </div>
  );
}
